import React, { useState, useRef, useEffect } from 'react';
import {
    Download,
    ChevronDown,
    FileJson,
    FileCode,
    FileType,
    Layers,
    Settings,
    X,
    Loader2
} from 'lucide-react';
import { exportDiff, exportJSON, exportPDF, exportEdiAnnotated, exportEdiHtml } from '../api';

/**
 * Export Dropdown Component - Enhanced with Descriptions
 *
 * Vertical list layout with format descriptions for better UX
 */
const ExportDropdown = ({
    result,
    source,
    target,
    modifiedTargetData,
    originalTargetFile,
    sourceFile,
    targetFile,
    sourceName = 'source',
    targetName = 'target',
    appliedRenames = new Set(),
    diffLines = [],
    addToast,
    defaultFormat = 'json',
    onExportConfig,
    appMode = 'compare'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(null);
    const [customFilename, setCustomFilename] = useState('');
    const [hoveredFormat, setHoveredFormat] = useState(null);
    const dropdownRef = useRef(null);

    // Generate timestamp-based filename
    const getDefaultFilename = (formatId) => {
        const now = new Date();
        const timestamp = now.toISOString().slice(0, 16).replace('T', '_').replace(':', '');

        if (formatId === 'corrected') {
            const originalName = originalTargetFile?.name || (targetName && !targetName.includes('target') ? targetName : '');
            const nameParts = originalName.split('.');
            const originalExt = nameParts.length > 1 ? `.${nameParts.pop()}` : '';
            const baseName = nameParts.join('.') || 'target';
            const extension = originalExt || (isEdi() ? '.txt' : '.json');
            return `${baseName}_CORR${extension}`;
        }

        const ext = {
            'diff': '.diff',
            'json': '.json',
            'html': '.html',
            'edi-annotated': '.edi.json',
            'csv': '.csv',
            'xml': '.xml',
            'pdf': '.pdf'
        }[formatId] || '.json';

        // Clean source name and strip off existing known extensions
        let safeSourceName = sourceName || 'source';
        const nameParts = safeSourceName.split('.');
        if (nameParts.length > 1) {
            nameParts.pop(); // remove extension
            safeSourceName = nameParts.join('.');
        }
        safeSourceName = safeSourceName.replace(/[^a-zA-Z0-9-_]/g, '_');

        return `${safeSourceName}_${timestamp}${ext}`;
    };

    // Helper to check if data is EDI
    const isEdi = () => {
        if (appMode === 'view') {
            return source?.detected_format?.startsWith('x12');
        }
        return source?.detected_format?.startsWith('x12') || target?.detected_format?.startsWith('x12');
    };

    // Reset filename when dropdown opens
    useEffect(() => {
        if (isOpen) {
            setCustomFilename('');
        }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close on escape
    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, []);

    const compareFormats = [
        {
            id: 'corrected',
            name: 'Formatted Corrected File',
            description: 'The updated file with all applied fixes inside the original filename',
            icon: Download,
            exactName: true,
            handler: async (fn) => {
                const data = modifiedTargetData || target;
                if (!data) throw new Error("No data available to download");

                let blob;
                const filename = fn || getDefaultFilename('corrected');

                if (isEdi()) {
                    // Reconstruct EDI string from segments
                    const segments = data.segments || [];
                    if (segments.length > 0) {
                        // Detect delimiters from first segment (usually ISA)
                        const firstSeg = segments[0];
                        const raw = firstSeg.raw || '';
                        let elementSep = '*';
                        let segmentTerm = '~';

                        if (raw.startsWith('ISA') && raw.length >= 106) {
                            elementSep = raw[3];
                            segmentTerm = raw[105];
                        }

                        // Use \n for readability, but many EDI systems prefer just the terminator
                        const ediContent = segments.map(seg => {
                            if (seg.elements && seg.segment_id) {
                                return [seg.segment_id, ...seg.elements].join(elementSep);
                            }
                            return seg.raw || '';
                        }).join(segmentTerm + '\n') + segmentTerm;

                        blob = new Blob([ediContent], { type: 'text/plain' });
                    } else {
                        throw new Error("EDI segments missing from data");
                    }
                } else {
                    blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                }

                return { blob, filename };
            }
        },
        {
            id: 'json',
            name: 'JSON Diff Report',
            description: 'Technical report for system integration',
            icon: FileJson,
            handler: async (fn) => {
                const blob = await exportJSON(source, target, sourceName, targetName, diffLines);
                return { blob, filename: fn || getDefaultFilename('json') };
            }
        },
        {
            id: 'diff',
            name: 'Git Unified Diff',
            description: 'Standard .diff patch file',
            icon: FileCode,
            handler: async (fn) => {
                const blob = await exportDiff(source, target, sourceName, targetName, diffLines);
                return { blob, filename: fn || getDefaultFilename('diff') };
            }
        },
        {
            id: 'html',
            name: 'Visual Side-by-Side',
            description: 'Shareable HTML comparison report',
            icon: FileType,
            handler: async (fn) => {
                let blob;
                if (isEdi()) {
                    blob = await exportEdiHtml(source, target, sourceName, targetName);
                } else {
                    const actions = result?.diff?.actions || [];
                    const additionsCount = actions.filter(a => a.action === 'add').length;
                    const deletionsCount = actions.filter(a => a.action === 'remove').length;
                    blob = await exportPDF(source, target, sourceName, targetName, diffLines, additionsCount, deletionsCount);
                }
                return { blob, filename: fn || getDefaultFilename('html') };
            }
        }
    ];

    const viewFormats = [
        {
            id: 'formatted',
            name: 'Formatted File',
            description: 'The original EDI formatted cleanly with newlines',
            icon: Download,
            exactName: true,
            handler: async (fn) => {
                const data = source;
                if (!data) throw new Error("No data available to download");

                let blob;
                const filename = fn || getDefaultFilename('formatted');

                if (isEdi()) {
                    // Reconstruct EDI string from segments
                    const segments = data.segments || [];
                    if (segments.length > 0) {
                        // Detect delimiters from first segment (usually ISA)
                        const firstSeg = segments[0];
                        const raw = firstSeg.raw || '';
                        let elementSep = '*';
                        let segmentTerm = '~';

                        if (raw.startsWith('ISA') && raw.length >= 106) {
                            elementSep = raw[3];
                            segmentTerm = raw[105];
                        }

                        // Use \n for readability
                        const ediContent = segments.map(seg => {
                            if (seg.elements && seg.segment_id) {
                                return [seg.segment_id, ...seg.elements].join(elementSep);
                            }
                            return seg.raw || '';
                        }).join(segmentTerm + '\n') + segmentTerm;

                        blob = new Blob([ediContent], { type: 'text/plain' });
                    } else {
                        throw new Error("EDI segments missing from data");
                    }
                } else {
                    blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                }

                return { blob, filename };
            }
        },
        {
            id: 'csv',
            name: 'CSV Data Extract',
            description: 'Tabular format for Excel',
            icon: Layers,
            ediOnly: true,
            handler: async (fn) => {
                const data = source;
                if (!data) throw new Error("No data available to export");

                let csvContent = '';
                const filename = fn || getDefaultFilename('csv');

                if (isEdi()) {
                    const segments = data.segments || [];
                    if (segments.length === 0) throw new Error("No segments to export");

                    // Find max elements to set columns
                    let maxElements = 0;
                    segments.forEach(seg => {
                        if (seg.elements && seg.elements.length > maxElements) {
                            maxElements = seg.elements.length;
                        }
                    });

                    // Header row
                    const headers = ['Segment ID', 'Segment Name'];
                    for (let i = 0; i < maxElements; i++) {
                        headers.push(`Element ${i + 1}`);
                    }

                    csvContent += headers.join(',') + '\n';

                    // Data rows
                    segments.forEach(seg => {
                        const row = [
                            `"${(seg.segment_id || '').replace(/"/g, '""')}"`,
                            `"${(seg.segment_name || '').replace(/"/g, '""')}"`
                        ];

                        // Add elements
                        for (let i = 0; i < maxElements; i++) {
                            const el = seg.elements?.[i];
                            if (el !== undefined && el !== null) {
                                // Escape quotes (el is likely a string here)
                                const val = (typeof el === 'object' ? (el.display || el.value || '') : String(el)).replace(/"/g, '""');
                                row.push(`"${val}"`);
                            } else {
                                row.push('');
                            }
                        }

                        csvContent += row.join(',') + '\n';
                    });
                } else {
                    throw new Error("CSV Export currently only supports EDI data");
                }

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                return { blob, filename };
            }
        },
        {
            id: 'json',
            name: 'JSON Data',
            description: 'Raw parsed JSON structure',
            icon: FileJson,
            handler: async (fn) => {
                const data = source;
                if (!data) throw new Error("No data available to export");

                const filename = fn || getDefaultFilename('json');
                const copyData = { ...data };
                // Strip raw string info if it's too large, or just send it all
                const blob = new Blob([JSON.stringify(copyData, null, 2)], { type: 'application/json' });
                return { blob, filename };
            }
        },
        {
            id: 'xml',
            name: 'XML Data',
            description: 'Structured XML format',
            icon: FileCode,
            ediOnly: true,
            handler: async (fn) => {
                const data = source;
                if (!data) throw new Error("No data available to export");

                let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<Document>\n';
                const filename = fn || getDefaultFilename('xml');

                if (isEdi()) {
                    const segments = data.segments || [];
                    segments.forEach(seg => {
                        const safeName = (seg.segment_name || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                        xmlContent += `  <Segment id="${seg.segment_id || ''}" name="${safeName}">\n`;
                        if (seg.elements) {
                            seg.elements.forEach((el, index) => {
                                const val = (typeof el === 'object' ? (el.display || el.value || '') : String(el)).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                                const position = typeof el === 'object' ? el.position : String(index + 1);
                                const name = typeof el === 'object' ? (el.name || `Element_${position}`).replace(/[^a-zA-Z0-9_]/g, '_') : `Element_${position}`;
                                xmlContent += `    <Element position="${position}" name="${name}">${val}</Element>\n`;
                            });
                        }
                        xmlContent += `  </Segment>\n`;
                    });
                } else {
                    xmlContent += `  <Data>XML export currently optimized for EDI formats</Data>\n`;
                }

                xmlContent += '</Document>';
                const blob = new Blob([xmlContent], { type: 'application/xml' });
                return { blob, filename };
            }
        },
        {
            id: 'pdf',
            name: 'Export to PDF',
            description: 'Download human-readable PDF',
            icon: FileType,
            ediOnly: true,
            handler: async (fn) => {
                const filename = fn || getDefaultFilename('pdf');

                if (isEdi()) {
                    const segments = source?.segments || [];

                    const htmlContent = `<!DOCTYPE html>
<html>
<head>
    <title>EDI Document</title>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; padding: 40px; color: #333; max-width: 1000px; margin: 0 auto; line-height: 1.5; }
        h1 { font-size: 24px; color: #111; margin-bottom: 5px; margin-top: 0; }
        .meta { color: #666; font-size: 14px; margin-bottom: 30px; border-bottom: 2px solid #eaeaea; padding-bottom: 20px; }
        .segment { margin-bottom: 16px; page-break-inside: avoid; }
        .seg-header { background: #f4f4f5; padding: 8px 12px; border-radius: 6px; margin-bottom: 8px; border: 1px solid #e4e4e7; display: flex; align-items: baseline; }
        .seg-id { font-weight: 700; color: #6b21a8; font-family: monospace; font-size: 1.1em; margin-right: 12px; }
        .seg-name { color: #52525b; font-size: 0.95em; }
        .elements-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 8px 16px; padding: 0 12px; }
        .element { display: flex; align-items: baseline; font-family: monospace; font-size: 13px; border-bottom: 1px dashed #e4e4e7; padding-bottom: 4px; }
        .el-pos { color: #a1a1aa; width: 30px; flex-shrink: 0; font-size: 0.9em; user-select: none; }
        .el-val { color: #18181b; font-weight: 600; word-break: break-all; }
        .el-name { color: #71717a; margin-right: 8px; font-style: italic; font-size: 0.9em; flex-shrink: 0; }
        .no-data { color: #a1a1aa; font-style: italic; font-size: 13px; padding-left: 12px; }

        @page { margin: 1.5cm; }
        @media print {
            body { padding: 0; max-width: 100% !important; }
            .no-print { display: none !important; }
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>EDI Data Export</h1>
        <div class="meta">Generated from DriftOps &bull; ${filename.replace('.html', '.pdf')}</div>
    </div>
    <div class="content">
        ${segments.map(seg => `
            <div class="segment">
                <div class="seg-header">
                    <span class="seg-id">${seg.segment_id}</span>
                    <span class="seg-name">${seg.segment_name || ''}</span>
                </div>
                ${(seg.elements && seg.elements.length > 0) ? `
                <div class="elements-grid">
                    ${seg.elements.map((el, index) => {
                        const val = (typeof el === 'object' ? (el.display || el.value || '') : String(el)).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                        if (!val) return ''; // Skip empty rendering if there's no data
                        const position = typeof el === 'object' ? el.position : String(index + 1).padStart(2, '0');
                        const name = typeof el === 'object' ? (el.name || '') : '';

                        return `
                        <div class="element">
                            <span class="el-pos">${position}</span>
                            ${name ? `<span class="el-name">${name}:</span>` : ''}
                            <span class="el-val">${val}</span>
                        </div>
                        `;
                    }).join('')}
                </div>
                ` : `<div class="no-data">No elements</div>`
                        }
            </div>
        `).join('')}
    </div>
    <script>
        // Auto-trigger print dialog after a short delay to ensure rendering
        setTimeout(() => {
            window.print();
        }, 500);
    </script>
</body>
</html>`;

                    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
                    // Sending to new tab since it triggers browser native print to PDF
                    return { blob, filename: filename.replace('.pdf', '.html'), openInNewTab: true };
                } else {
                    throw new Error("PDF Export currently only supports EDI data");
                }
            }
        }
    ];

    const exportFormats = (appMode === 'view' ? viewFormats : compareFormats)
        .filter(f => !f.ediOnly || isEdi());

    const getPreviewFilename = () => {
        // Return placeholder based on hovered format, or first format
        const formatItem = hoveredFormat || exportFormats[0];
        const originalName = appMode === 'view' ? sourceFile?.name : (targetFile?.name || originalTargetFile?.name);

        if (formatItem?.exactName && originalName) {
            return originalName;
        }
        return getDefaultFilename(formatItem?.id || (appMode === 'view' ? 'formatted' : 'corrected'));
    };

    const handleExport = async (format) => {
        // Use the native source filename with no prefix/suffix modifications if format requests it
        const originalName = appMode === 'view' ? sourceFile?.name : (targetFile?.name || originalTargetFile?.name);

        const filename = customFilename.trim()
            ? customFilename.trim()
            : (format.exactName && originalName ? originalName : getDefaultFilename(format.id));

        setLoading(format.id);

        try {
            const resultObj = await format.handler(filename);

            if (resultObj.openInNewTab) {
                const url = URL.createObjectURL(resultObj.blob);
                window.open(url, '_blank');
                setIsOpen(false);
                addToast?.('Opened print dialog for PDF', 'success');
                return;
            }

            const { blob } = resultObj;

            // Use File System Access API if available
            if (window.showSaveFilePicker) {
                try {
                    const acceptConfig = {};
                    if (blob.type === 'application/json') {
                        acceptConfig['application/json'] = ['.json'];
                    } else if (blob.type === 'text/html') {
                        acceptConfig['text/html'] = ['.html'];
                    } else if (blob.type === 'text/csv' || blob.type === 'text/csv;charset=utf-8;') {
                        acceptConfig['text/csv'] = ['.csv'];
                    } else if (blob.type === 'application/xml') {
                        acceptConfig['application/xml'] = ['.xml'];
                    } else {
                        acceptConfig['text/plain'] = [
                            format.id === 'html' ? '.html' :
                                format.id === 'json' ? '.json' :
                                    format.id === 'diff' ? '.diff' :
                                        format.id === 'pdf' ? '.html' : '.txt'
                        ];
                    }

                    const handle = await window.showSaveFilePicker({
                        suggestedName: filename.replace('.pdf', '.html'),
                        types: [{
                            description: format.name,
                            accept: acceptConfig
                        }]
                    });
                    const writable = await handle.createWritable();
                    await writable.write(blob);
                    await writable.close();
                    addToast?.(`Exported to ${handle.name}`, 'success');
                } catch (err) {
                    if (err.name !== 'AbortError') {
                        throw err;
                    }
                    // If user aborts, do nothing (don't fallback to download)
                }
            } else {
                // Fallback to standard download
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => URL.revokeObjectURL(url), 150);
                addToast?.(`Downloaded ${filename}`, 'success');
            }

            setIsOpen(false);
        } catch (error) {
            console.error('Export failed:', error);
            addToast?.(`Export failed: ${error.message || 'Unknown error'}`, 'error');
        } finally {
            setLoading(null);
        }
    };

    const handleConfigExport = () => {
        setIsOpen(false);
        onExportConfig?.();
    };

    if (!result) return null;

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-1.5 text-xs bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors shadow-sm"
            >
                <Download size={14} />
                Download
                <ChevronDown
                    size={12}
                    className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-72 bg-[#252526] rounded-xl shadow-xl border border-[#3c3c3c] z-50 overflow-hidden">
                    {/* Header */}
                    <div className="px-3 py-2 border-b border-[#3c3c3c] bg-[#1e1e1e]">
                        <span className="text-xs font-medium text-[#808080] uppercase tracking-wide">
                            Download Results
                        </span>
                    </div>

                    {/* Format List */}
                    <div className="py-1">
                        {exportFormats.map((format) => {
                            const Icon = format.icon;
                            const isLoading = loading === format.id;
                            return (
                                <button
                                    key={format.id}
                                    onClick={() => handleExport(format)}
                                    onMouseEnter={() => setHoveredFormat(format)}
                                    onMouseLeave={() => setHoveredFormat(null)}
                                    disabled={isLoading}
                                    className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-[#3c3c3c] transition-colors disabled:opacity-50"
                                >
                                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-[#3c3c3c] text-[#cccccc]">
                                        {isLoading ? (
                                            <Loader2 size={16} className="animate-spin" />
                                        ) : (
                                            <Icon size={16} />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-white">
                                            {format.name}
                                        </div>
                                        <div className="text-xs text-[#808080]">
                                            {format.description}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    {/* Filename Input */}
                    <div className="px-3 py-2 border-t border-[#3c3c3c]">
                        <div className="mb-2">
                            <label className="text-[10px] uppercase text-gray-500 font-bold mb-1 block">Filename</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={customFilename}
                                    onChange={(e) => setCustomFilename(e.target.value)}
                                    placeholder={getPreviewFilename()}
                                    className="w-full px-2.5 py-1.5 pr-7 text-xs bg-[#1e1e1e] border-[#4c4c4c] rounded text-white placeholder-[#666] focus:outline-none focus:ring-1 focus:ring-blue-500"
                                />
                                {customFilename && (
                                    <button
                                        onClick={() => setCustomFilename('')}
                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                                    >
                                        <X size={12} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Export Location UI */}
                        <div>
                            <div className="flex justify-between items-baseline mb-1">
                                <label className="text-[10px] uppercase text-gray-500 font-bold block">Download Location</label>
                                {!window.showSaveFilePicker && (
                                    <span className="text-[9px] text-blue-400 cursor-pointer hover:underline" onClick={() => addToast('Go to Settings > General > Files and Applications > "Always ask you where to save files"', 'info')}>
                                        How to change?
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-1">
                                <div className="flex-1 px-2.5 py-1.5 text-xs bg-[#333] border-[#4c4c4c] rounded text-gray-300 italic truncate cursor-default" title="Location is managed by your browser settings">
                                    {window.showSaveFilePicker ? 'Prompt on Download' : 'Browser Default (Downloads)'}
                                </div>
                                {window.showSaveFilePicker && (
                                    <div className="px-2 py-1.5 bg-[#3c3c3c] border-[#4c4c4c] rounded text-gray-300 text-xs flex items-center justify-center">
                                        ...
                                    </div>
                                )}
                            </div>
                            {!window.showSaveFilePicker && (
                                <p className="text-[9px] text-gray-400 mt-1">
                                    Firefox/Safari: Enable "Always ask where to save" in browser settings to pick location.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Config Download Link */}
                    {result.diff?.suggested_renames?.length > 0 && (
                        <button
                            onClick={handleConfigExport}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#808080] hover:bg-[#3c3c3c] border-t border-[#3c3c3c] transition-colors"
                        >
                            <Settings size={12} />
                            Download Rename Config (.json) ({appliedRenames.size} applied)
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default ExportDropdown;
